/**
 * 通用错误提示工具函数
 * 用于统一处理各种业务场景的错误提示
 */

import { Message } from 'element-ui'

/**
 * 显示友好的错误提示
 * @param {Error|Object} error - 错误对象
 * @param {Object} options - 配置选项
 * @param {string} options.defaultMessage - 默认提示消息
 * @param {boolean} options.showConsole - 是否显示控制台日志，默认 true
 * @param {Function} options.onAuthError - 认证错误回调（可选）
 * @param {Function} options.onServerError - 服务器错误回调（可选）
 * @param {Function} options.onNetworkError - 网络错误回调（可选）
 * 
 * @example
 * // 基本用法
 * showError(err)
 * 
 * @example
 * // 自定义配置
 * showError(err, {
 *   defaultMessage: '操作失败',
 *   showConsole: false
 * })
 * 
 * @example
 * // 带回调
 * showError(err, {
 *   onAuthError: () => {
 *     // 自定义认证错误处理
 *   }
 * })
 */
export function showError(error, options = {}) {
  const {
    defaultMessage = '操作失败，请稍后重试',
    showConsole = true,
    onAuthError,
    onServerError,
    onNetworkError
  } = options

  if (!error) {
    console.warn('showError: 未传入错误对象')
    return
  }

  // 记录错误日志
  if (showConsole) {
    console.error('错误详情:', error)
  }

  // 1. 检查业务错误码
  if (error.code === 11006) {
    const msg = '登录已过期，请重新登录'
    Message.error(msg)
    if (onAuthError) onAuthError(error)
    return
  }

  // 2. 检查 HTTP 状态码
  if (error.response) {
    const status = error.response.status
    
    // 401 - 未授权
    if (status === 401) {
      const msg = '认证失败，请重新登录'
      Message.error(msg)
      if (onAuthError) onAuthError(error)
      return
    }
    
    // 403 - 禁止访问
    if (status === 403) {
      const msg = '无权访问，请联系管理员'
      Message.error(msg)
      return
    }
    
    // 404 - 资源不存在
    if (status === 404) {
      const msg = '请求的资源不存在'
      Message.error(msg)
      return
    }
    
    // 500 - 服务器错误
    if (status === 500) {
      const msg = '服务器异常，请稍后重试或联系管理员'
      Message.error(msg)
      if (onServerError) onServerError(error)
      return
    }
    
    // 502/503/504 - 网关错误
    if ([502, 503, 504].includes(status)) {
      const msg = '服务暂时不可用，请稍后重试'
      Message.error(msg)
      if (onServerError) onServerError(error)
      return
    }
  }

  // 2.5 检查错误消息中是否包含 HTTP 401 相关信息
  if (error.msg && typeof error.msg === 'string' && error.msg.includes('HTTP 401')) {
    const msg = '认证失败，请重新登录'
    Message.error(msg)
    if (onAuthError) onAuthError(error)
    return
  }
  
  if (error.message && typeof error.message === 'string' && error.message.includes('HTTP 401')) {
    const msg = '认证失败，请重新登录'
    Message.error(msg)
    if (onAuthError) onAuthError(error)
    return
  }

  // 3. 检查网络错误
  if (error.message) {
    // 超时
    if (error.message.includes('timeout')) {
      const msg = '请求超时，请检查网络连接后重试'
      Message.error(msg)
      if (onNetworkError) onNetworkError(error)
      return
    }
    
    // 网络错误
    if (error.message.includes('Network Error')) {
      const msg = '网络连接失败，请检查网络设置'
      Message.error(msg)
      if (onNetworkError) onNetworkError(error)
      return
    }
  }

  // 4. 使用后端返回的错误消息
  if (error.msg) {
    Message.error(error.msg)
    return
  }

  // 5. 使用错误对象的 message
  if (error.message && error.message !== 'Network Error') {
    Message.error(error.message)
    return
  }

  // 6. 使用默认消息
  Message.error(defaultMessage)
}

/**
 * 显示成功提示
 * @param {string} message - 提示消息
 * @param {Object} options - 配置选项
 */
export function showSuccess(message, options = {}) {
  const {
    duration = 3000,
    showClose = true
  } = options
  
  Message.success({
    message,
    duration,
    showClose
  })
}

/**
 * 显示警告提示
 * @param {string} message - 提示消息
 * @param {Object} options - 配置选项
 */
export function showWarning(message, options = {}) {
  const {
    duration = 3000,
    showClose = true
  } = options
  
  Message.warning({
    message,
    duration,
    showClose
  })
}

/**
 * 显示信息提示
 * @param {string} message - 提示消息
 * @param {Object} options - 配置选项
 */
export function showInfo(message, options = {}) {
  const {
    duration = 3000,
    showClose = true
  } = options
  
  Message.info({
    message,
    duration,
    showClose
  })
}

/**
 * 根据错误码获取友好的错误消息
 * @param {number|string} code - 错误码
 * @param {string} customMsg - 自定义消息（优先级最高）
 * @returns {string} 友好的错误消息
 */
export function getErrorMessage(code, customMsg = '') {
  if (customMsg) {
    return customMsg
  }

  const errorMessages = {
    // 认证相关
    11006: '用户不存在，请重新登录',
    401: '登录已过期，请重新登录',
    403: '无权访问，请联系管理员',
    
    // 客户端错误
    400: '请求参数错误',
    404: '请求的资源不存在',
    405: '请求方法不允许',
    408: '请求超时',
    
    // 服务器错误
    500: '服务器内部错误',
    502: '网关错误',
    503: '服务暂时不可用',
    504: '网关超时',
    
    // 业务错误
    10001: '数据验证失败',
    10002: '权限不足',
    10003: '操作频繁，请稍后重试',
    10004: '资源已被删除',
    10005: '资源已被修改，请刷新后重试'
  }

  return errorMessages[code] || `系统错误 (代码: ${code})`
}

/**
 * 批量处理多个 API 请求的错误
 * @param {Promise[]} promises - Promise 数组
 * @param {Object} options - 配置选项
 * @returns {Promise<Array>} 结果数组
 */
export async function handleBatchRequests(promises, options = {}) {
  const {
    stopOnError = false,  // 遇到错误是否停止
    onError,              // 错误回调
    showMessage = true    // 是否显示错误消息
  } = options

  const results = []
  
  for (let i = 0; i < promises.length; i++) {
    try {
      const result = await promises[i]()
      results.push({ success: true, data: result, index: i })
    } catch (error) {
      results.push({ success: false, error, index: i })
      
      if (showMessage) {
        showError(error)
      }
      
      if (onError) {
        onError(error, i)
      }
      
      if (stopOnError) {
        break
      }
    }
  }
  
  return results
}

/**
 * 带重试的请求
 * @param {Function} requestFn - 请求函数
 * @param {Object} options - 配置选项
 * @returns {Promise} 请求结果
 */
export async function retryRequest(requestFn, options = {}) {
  const {
    maxRetries = 3,           // 最大重试次数
    retryDelay = 1000,        // 重试间隔（毫秒）
    backoff = true,           // 是否使用指数退避
    onRetry                   // 重试回调
  } = options

  let lastError
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn()
    } catch (error) {
      lastError = error
      
      // 如果是最后一次尝试，抛出错误
      if (attempt === maxRetries) {
        throw error
      }
      
      // 调用重试回调
      if (onRetry) {
        onRetry(error, attempt, maxRetries)
      }
      
      // 计算延迟时间（指数退避）
      const delay = backoff ? retryDelay * Math.pow(2, attempt - 1) : retryDelay
      
      // 等待后重试
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  throw lastError
}

// 导出所有工具函数
export default {
  showError,
  showSuccess,
  showWarning,
  showInfo,
  getErrorMessage,
  handleBatchRequests,
  retryRequest
}
